import { GiB, kvBytesPerSequence } from "./core.ts";
import { GPUS, RUNTIMES, defaultEngine, formatOk, isApple, usableMemGiB, type Gpu } from "./hardware.ts";
import { minGpus, weightBytes } from "./memory.ts";
import type { ModelPreset } from "./models.ts";

// ── Fiches par modèle (pages /vram-requirements/…) ──────────────────────────
// Scénario fixe et explicite pour que les chiffres soient comparables d'une fiche à
// l'autre : une requête, cache KV en BF16. Le nombre de GPU utilise le moteur
// réaliste pour chaque carte : vLLM en datacenter, llama.cpp sur poste et Mac.

export const SHEET_FORMATS = [
  { id: "bf16", label: "BF16 / FP16", bits: 16 },
  { id: "fp8", label: "FP8 / INT8", bits: 8 },
  { id: "q4_k_m", label: "4-bit (GGUF Q4_K_M)", bits: 4.85 },
] as const;

// Les machines à mémoire unifiée de 128 Go (DGX Spark, Ryzen AI Max+) : ce que les gens cherchent pour un modèle local.
export const SHEET_GPUS = ["rtx-4090", "rtx-5090", "rtx-pro-6000", "mac-m5-max-128", "strix-halo", "dgx-spark", "h100", "h200", "b200"];
export const SHEET_CONTEXT = 8192;

export type ModelSheet = {
  formats: { id: string; label: string; bits: number; weightsGiB: number; withContextGiB: { ctx: number; gib: number }[] }[];
  kv: { ctx: number; bf16GiB: number; fp8GiB: number }[];
  stateGiB: number;
  /** supported : le GPU calcule ce format nativement (faux pour un checkpoint FP8 ou FP4 sans support). */
  gpus: { id: string; label: string; vram: number; engine: string; counts: (number | null)[]; supported: boolean[]; labels: string[] }[];
  /** KV du modèle face à un transformeur « classique » de même forme (toutes couches en attention complète). */
  kvSavingVsFullAttention: number;
};

/** Fewest GPUs of this type for one request at SHEET_CONTEXT : vLLM en datacenter, llama.cpp ailleurs. */
export function gpusNeeded(model: ModelPreset, g: Gpu, weightBits: number): number | null {
  const rt = RUNTIMES.find((r) => r.id === defaultEngine(g))!;
  return minGpus({
    model, mode: "inference", weightBits, kvBytes: 2, context: SHEET_CONTEXT, sequences: 1, layerSplit: rt.id === "llamacpp",
    tp: 1, pp: 1, vramGiB: usableMemGiB(g), utilization: rt.utilization, overheadGiB: rt.overheadGiB, target: { engine: rt.id, gpu: g },
    gradientCheckpointing: true, shardStates: true, loraFraction: 0.01,
  });
}

/** Plus petit Mac qui tient le modèle seul à ce format (une requête, SHEET_CONTEXT, llama.cpp). À mémoire
 *  égale, le premier de GPUS, donc la puce la plus récente. */
export function smallestMac(model: ModelPreset, weightBits: number): Gpu | undefined {
  return GPUS.filter((g) => isApple(g) && gpusNeeded(model, g, weightBits) === 1).sort((a, b) => a.vram - b.vram)[0];
}
/** Le Mac à la plus grande mémoire GPU (Mac Studio Ultra). */
export const LARGEST_MAC = GPUS.filter(isApple).reduce((a, b) => (b.vram > a.vram ? b : a));

/** Format d'une colonne de la fiche pour un moteur : « 8-bit » est FP8 (INT8 sans FP8) avec vLLM,
 *  Q8_0 avec llama.cpp ; « 4-bit » est Q4_K_M avec llama.cpp. */
export function sheetLabel(id: string, engine: string, g: Pick<Gpu, "fp8">, fallback: string) {
  if (id === "fp8") return engine === "llamacpp" ? "Q8_0" : g.fp8 ? "FP8" : "INT8";
  if (id === "q4_k_m") return engine === "llamacpp" ? "Q4_K_M" : "4-bit";
  return fallback.replace(" / FP16", "");
}

export function modelSheet(model: ModelPreset): ModelSheet {
  const formats = [
    ...(model.native ? [{ id: "native", label: model.native.label, bits: 0 }] : []),
    ...SHEET_FORMATS,
  ];
  const contexts = [8192, 32768, 131072, ...(Number.isFinite(model.maxContext) && model.maxContext > 131072 ? [model.maxContext] : [])]
    .filter((c) => c <= model.maxContext);
  // Contexte plus court que 8K (modèle ancien) : sa propre limite, plutôt que des tableaux vides.
  if (!contexts.length) contexts.push(model.maxContext);
  const state = model.stateBytesPerSeq ?? 0;

  const gpus = SHEET_GPUS.map((id) => {
    const g = GPUS.find((x) => x.id === id)!;
    const counts = formats.map((f) => gpusNeeded(model, g, f.bits));
    const engine = defaultEngine(g);
    // Le checkpoint officiel (FP8, FP4) : seulement si ce moteur le charge et, hors llama.cpp, si le GPU
    // le calcule nativement. Libellé de chaque colonne pour ce moteur : FP8 (INT8 sans FP8) ou Q8_0.
    const supported = formats.map((f) => f.id !== "native" || formatOk(g, engine, model, "native"));
    const labels = formats.map((f) => sheetLabel(f.id, engine, g, f.label));
    // « Ryzen AI Max+ 395 / 392 / 388 » : même mémoire, mêmes nombres ; le premier nom suffit (colonne étroite sur mobile).
    return { id, label: g.label.split(" · ")[0].replace(/( \/ \d{3})+/, ""), vram: g.vram, engine: engine === "vllm" ? "vLLM" : "llama.cpp", counts, supported, labels };
  });

  const full = model.layers * Math.max(...model.kv.map((g) => g.width)) * 32768 * 2;
  return {
    formats: formats.map((f) => {
      const w = weightBytes(model, f.bits);
      return {
        ...f,
        weightsGiB: w / GiB,
        withContextGiB: contexts.map((ctx) => ({ ctx, gib: (w + kvBytesPerSequence(model, ctx, 2) + state) / GiB })),
      };
    }),
    kv: contexts.map((ctx) => ({ ctx, bf16GiB: kvBytesPerSequence(model, ctx, 2) / GiB, fp8GiB: kvBytesPerSequence(model, ctx, 1) / GiB })),
    stateGiB: state / GiB,
    gpus,
    kvSavingVsFullAttention: full / kvBytesPerSequence(model, 32768, 2),
  };
}
